import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  TrendingUp, Users, Target, BarChart3, PieChart, Calendar, RefreshCw,
  ArrowUpRight, AlertTriangle, CheckCircle2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, LineChart, Line, Legend
} from "recharts";
import Layout from "../components/Layout";
import { API, useAuth } from "../App";
import { DateRangePicker, openLeadsTab } from "../components/DateRangePicker";
import { appendDateParams } from "../utils/dateFilters";
import { InfoTip } from "../components/InfoTip";
import SourceBasisToggle from "../components/SourceBasisToggle";
import PeriodDelta from "../components/PeriodDelta";
import PerformanceAlertTile from "../components/PerformanceAlertTile";
import WinSurgeTile from "../components/WinSurgeTile";
import { last30Days, previousPeriod } from "../lib/dateDefaults";
import { useUrlFilters } from "../hooks/useUrlFilters";

const _DEF = last30Days();

const AnalyticsPage = () => {
  const { user } = useAuth();
  const isManager = !!user && ["admin", "ceo", "sales_head", "hod_sales", "marketing_manager"].includes(user.role);
  const isAdmin = !!user && ["admin", "ceo", "sales_head", "hod_sales"].includes(user.role);
  const [stats, setStats] = useState(null);
  const [prevStats, setPrevStats] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [trends, setTrends] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [urlFilters, setUrlFilters] = useUrlFilters({
    date_from: _DEF.from,
    date_to: _DEF.to,
    source_basis: "latest",
  });
  const dateFrom = urlFilters.date_from;
  const setDateFrom = (v) => setUrlFilters({ date_from: v });
  const dateTo = urlFilters.date_to;
  const setDateTo = (v) => setUrlFilters({ date_to: v });
  const sourceBasis = urlFilters.source_basis;
  const setSourceBasis = (v) => setUrlFilters({ source_basis: v });

  useEffect(() => {
    fetchAnalytics();
  }, [dateFrom, dateTo, sourceBasis]);

  const fetchAnalytics = async () => {
    try {
      const dp = new URLSearchParams();
      appendDateParams(dp, dateFrom, dateTo);
      dp.append("source_basis", sourceBasis);
      const qs = dp.toString();
      const [statsRes, pipelineRes, trendsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard${qs ? `?${qs}` : ""}`),
        axios.get(`${API}/analytics/pipeline${qs ? `?${qs}` : ""}`),
        axios.get(`${API}/analytics/trends?days=30${qs ? `&${qs}` : ""}`)
      ]);
      
      setStats(statsRes.data);
      setPipeline(pipelineRes.data);
      setTrends(trendsRes.data);

      // Previous-period fetch for KPI deltas (silent — failure leaves badges hidden).
      const prev = previousPeriod(dateFrom, dateTo);
      if (prev.from && prev.to) {
        try {
          const pdp = new URLSearchParams();
          appendDateParams(pdp, prev.from, prev.to);
          pdp.append("source_basis", sourceBasis);
          const pres = await axios.get(`${API}/analytics/dashboard?${pdp.toString()}`);
          setPrevStats(pres.data);
        } catch { setPrevStats(null); }
      } else {
        setPrevStats(null);
      }
    } catch (error) {
      console.error("Fetch analytics error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchForecast = async (forceRefresh = false) => {
    setForecastLoading(true);
    try {
      const res = await axios.get(`${API}/ai/forecast${forceRefresh ? '?force_refresh=true' : ''}`);
      setForecast(res.data);
    } catch (error) {
      console.error("Forecast fetch error:", error);
    } finally {
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    fetchForecast();
  }, []);

  const COLORS = ['#0F172A', '#D97706', '#059669', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444'];

  const sourceDataRaw = stats?.source_counts ? 
    Object.entries(stats.source_counts).map(([name, value]) => ({ name: name.replace(/_/g, ' '), value })).sort((a, b) => b.value - a.value) : [];
  const sourceTotal = sourceDataRaw.reduce((s, d) => s + d.value, 0);
  // Group small slices (<3%) into "Other"
  const threshold = sourceTotal * 0.03;
  const sourceMain = sourceDataRaw.filter(d => d.value >= threshold);
  const otherValue = sourceDataRaw.filter(d => d.value < threshold).reduce((s, d) => s + d.value, 0);
  const sourceData = otherValue > 0 ? [...sourceMain, { name: "Other", value: otherValue }] : sourceMain;

  const scoreData = stats?.score_counts ?
    Object.entries(stats.score_counts).map(([name, value]) => ({ name, value })) : [];

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <Card key={i} className="bg-white border-slate-200 animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-slate-200 rounded w-3/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">Analytics</h1>
            <p className="text-slate-500 mt-1">Track your sales performance</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <SourceBasisToggle value={sourceBasis} onChange={setSourceBasis} />
            <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          </div>
        </div>

        {/* Performance Alert Tile (manager+ roles only) */}
        {isManager && (
          <PerformanceAlertTile
            showRunNow={isAdmin}
            onOpen={() => { /* future: open detail modal */ }}
          />
        )}

        {/* Win Surge Tile — visible to leaders (always) and the rep whose surge it is */}
        <WinSurgeTile />

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Leads</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-3xl font-bold text-slate-900 font-['Manrope']">{stats?.total_leads || 0}</p>
                    <PeriodDelta current={stats?.total_leads} prior={prevStats?.total_leads} />
                  </div>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-slate-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ stage: "booked", date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Conversion Rate</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-3xl font-bold text-emerald-600 font-['Manrope']">{stats?.conversion_rate || 0}%</p>
                    <PeriodDelta current={stats?.conversion_rate} prior={prevStats?.conversion_rate} />
                  </div>
                </div>
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => openLeadsTab({ score: "hot", date_from: dateFrom, date_to: dateTo })}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Hot Leads</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <p className="text-3xl font-bold text-red-600 font-['Manrope']">{stats?.hot_leads || 0}</p>
                    <PeriodDelta current={stats?.hot_leads} prior={prevStats?.hot_leads} />
                  </div>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Pending Tasks</p>
                  <p className="text-3xl font-bold text-amber-600 font-['Manrope'] mt-1">{stats?.pending_tasks || 0}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Funnel */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Sales Pipeline</CardTitle>
              <InfoTip text="Shows how many leads are in each stage of your sales pipeline. Click any bar to see those leads. A healthy funnel narrows gradually from left to right." />
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={pipeline} layout="vertical" onClick={(e) => { if (e?.activePayload?.[0]) openLeadsTab({ stage: e.activePayload[0].payload.stage, date_from: dateFrom, date_to: dateTo }); }} style={{ cursor: "pointer" }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" />
                  <YAxis dataKey="stage" type="category" width={80} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px'
                    }} 
                  />
                  <Bar dataKey="count" fill="#0F172A" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Lead Sources */}
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-['Manrope']">Lead Sources</CardTitle>
              <InfoTip text="Distribution of leads by acquisition channel (website, referral, walk-in, etc.). Click any slice to see leads from that source." />
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-shrink-0" style={{ width: 220, height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie
                        data={sourceData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={95}
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                        onClick={(entry) => { if (entry?.name && entry.name !== "Other") openLeadsTab({ source: entry.name.toLowerCase().replace(/ /g, '_'), date_from: dateFrom, date_to: dateTo }); }}
                        style={{ cursor: "pointer" }}
                      >
                        {sourceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [value, "Leads"]} />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 w-full space-y-1.5 max-h-[220px] overflow-y-auto">
                  {sourceData.map((entry, index) => {
                    const pct = sourceTotal > 0 ? ((entry.value / sourceTotal) * 100).toFixed(1) : 0;
                    return (
                      <div
                        key={entry.name}
                        className={`flex items-center justify-between px-2.5 py-1.5 rounded-md hover:bg-slate-50 transition-colors ${entry.name !== "Other" ? "cursor-pointer" : ""}`}
                        onClick={() => { if (entry.name !== "Other") openLeadsTab({ source: entry.name.toLowerCase().replace(/ /g, '_'), date_from: dateFrom, date_to: dateTo }); }}
                        data-testid={`source-legend-${entry.name.toLowerCase().replace(/ /g, '-')}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="text-sm text-slate-700 capitalize">{entry.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{entry.value}</span>
                          <span className="text-xs text-slate-400 w-10 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trends Chart */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold font-['Manrope']">Lead Trends (Last 30 Days)</CardTitle>
            <InfoTip text="Daily count of new leads created over the last 30 days. Click any data point to see leads from that date. Rising trend = growing pipeline." />
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends} onClick={(e) => { if (e?.activePayload?.[0]) { const d = e.activePayload[0].payload.date; openLeadsTab({ date_from: d, date_to: d }); }}} style={{ cursor: "pointer" }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                />
                <YAxis />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                />
                <Line 
                  type="monotone" 
                  dataKey="leads" 
                  stroke="#D97706" 
                  strokeWidth={2}
                  dot={{ fill: '#D97706', strokeWidth: 2 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* AI Booking Forecast */}
        <Card className="bg-white border-slate-200 shadow-sm" data-testid="forecast-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              <CardTitle className="text-lg font-semibold font-['Manrope']">Booking Forecast</CardTitle>
              {forecast?.mode && (
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  forecast.mode === 'ai' ? 'bg-violet-100 text-violet-700' :
                  forecast.mode === 'rule_based' ? 'bg-blue-100 text-blue-700' :
                  'bg-slate-100 text-slate-500'
                }`}>{forecast.mode_label}</span>
              )}
              <InfoTip text={
                forecast?.mode === 'ai'
                  ? "Generated by GPT-5.2 analyzing your pipeline stages, lead scores, inflow velocity, and historical conversion rate. Cached for 6 hours — click refresh for a new prediction."
                  : forecast?.mode === 'rule_based'
                  ? "Calculated using fixed conversion probabilities per stage. E.g., 50% of negotiation leads convert in 30 days, 15% of site visit leads in 60 days. Add 50+ leads to unlock AI-powered forecasts."
                  : "Forecasting requires at least 10 leads in your pipeline."
              } />
            </div>
            <Button variant="ghost" size="sm" onClick={() => fetchForecast(true)} disabled={forecastLoading} data-testid="refresh-forecast-btn">
              <RefreshCw className={`w-4 h-4 ${forecastLoading ? 'animate-spin' : ''}`} />
            </Button>
          </CardHeader>
          <CardContent>
            {forecastLoading && !forecast ? (
              <div className="py-8 text-center text-slate-500">Generating forecast...</div>
            ) : forecast?.mode === 'empty' ? (
              <div className="py-10 text-center">
                <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-base font-medium text-slate-700">Not enough data yet</p>
                <p className="text-sm text-slate-500 mt-1">Add at least 10 leads to unlock booking forecasts.</p>
                <p className="text-xs text-slate-400 mt-2">Currently: {forecast.total_leads} / {forecast.minimum_required} leads</p>
              </div>
            ) : forecast ? (
              <div className="space-y-6">
                {/* Mode description */}
                {forecast.mode_description && (
                  <p className="text-xs text-slate-400 italic">{forecast.mode_description}</p>
                )}

                {/* Forecast cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: '30 Day', data: forecast.forecast_30d, color: 'bg-emerald-50 border-emerald-200' },
                    { label: '60 Day', data: forecast.forecast_60d, color: 'bg-blue-50 border-blue-200' },
                    { label: '90 Day', data: forecast.forecast_90d, color: 'bg-indigo-50 border-indigo-200' }
                  ].map((period) => (
                    <div key={period.label} className={`p-4 rounded-xl border ${period.color}`} data-testid={`forecast-${period.label.replace(' ', '-').toLowerCase()}`}>
                      <div className="flex items-center gap-1">
                        <p className="text-sm font-medium text-slate-500">{period.label} Forecast</p>
                        <InfoTip text={`Predicted bookings in the next ${period.label.toLowerCase()}. Confidence reflects how much pipeline data supports this estimate.`} />
                      </div>
                      <p className="text-3xl font-bold text-slate-900 font-['Manrope'] mt-1">{period.data?.bookings ?? '—'}</p>
                      <p className="text-xs mt-1">
                        <span className={`font-medium ${
                          period.data?.confidence === 'high' ? 'text-emerald-600' :
                          period.data?.confidence === 'medium' ? 'text-amber-600' : 'text-slate-500'
                        }`}>
                          {period.data?.confidence || '—'} confidence
                        </span>
                      </p>
                    </div>
                  ))}
                </div>

                {/* Pipeline health + revenue */}
                <div className="flex flex-wrap items-center gap-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full ${
                    forecast.pipeline_health === 'healthy' ? 'bg-emerald-100 text-emerald-700' :
                    forecast.pipeline_health === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`} data-testid="pipeline-health-badge">
                    {forecast.pipeline_health === 'healthy' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                    Pipeline: {forecast.pipeline_health?.replace(/_/g, ' ')}
                  </span>
                  {forecast.revenue_estimate_30d && (
                    <span className="text-sm text-slate-600">
                      Est. 30d Revenue: <span className="font-semibold text-slate-900">{forecast.revenue_estimate_30d}</span>
                    </span>
                  )}
                  <span className="text-sm text-slate-500">
                    Velocity: {forecast.pipeline_snapshot?.weekly_velocity} leads/week
                  </span>
                </div>

                {/* Conversion rates used (rule-based only) */}
                {forecast.conversion_rates_used && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-1 mb-2">
                      <p className="text-xs font-semibold text-slate-600">Conversion Rates Used (30d/60d/90d)</p>
                      <InfoTip text="These are the fixed conversion probabilities applied per stage. E.g., '50%/65%/70%' for negotiation means 50% of negotiation leads are expected to book within 30 days." />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(forecast.conversion_rates_used).map(([stage, rates]) => (
                        <span key={stage} className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-600">
                          {stage.replace(/_/g, ' ')}: <span className="font-mono font-medium">{rates}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Risks and actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {forecast.key_risks?.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center gap-1 mb-2">
                        <p className="text-sm font-semibold text-red-700">Key Risks</p>
                        <InfoTip text="Potential blockers that could prevent bookings. Address these to improve your conversion pipeline." />
                      </div>
                      <ul className="space-y-1">
                        {forecast.key_risks.map((r, i) => (
                          <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {r}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {forecast.acceleration_actions?.length > 0 && (
                    <div className="bg-emerald-50 rounded-lg p-4">
                      <div className="flex items-center gap-1 mb-2">
                        <p className="text-sm font-semibold text-emerald-700">Acceleration Actions</p>
                        <InfoTip text="Recommended next steps to speed up conversions and move leads through the pipeline faster." />
                      </div>
                      <ul className="space-y-1">
                        {forecast.acceleration_actions.map((a, i) => (
                          <li key={i} className="text-sm text-emerald-600 flex items-start gap-2">
                            <ArrowUpRight className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {a}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-slate-500">Forecast data unavailable</div>
            )}
          </CardContent>
        </Card>

        {/* Score Distribution */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-lg font-semibold font-['Manrope']">Lead Score Distribution</CardTitle>
              <InfoTip text="Hot = high intent, ready to buy. Warm = interested, needs nurturing. Nurture = early stage, long-term prospect. Dormant = inactive, may need re-engagement. New = recently added, not yet scored." />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { name: 'hot', label: 'Hot', color: 'bg-red-500', bgColor: 'bg-red-50' },
                { name: 'warm', label: 'Warm', color: 'bg-amber-500', bgColor: 'bg-amber-50' },
                { name: 'nurture', label: 'Nurture', color: 'bg-blue-500', bgColor: 'bg-blue-50' },
                { name: 'dormant', label: 'Dormant', color: 'bg-slate-400', bgColor: 'bg-slate-50' },
                { name: 'new', label: 'New', color: 'bg-emerald-500', bgColor: 'bg-emerald-50' }
              ].map((score) => (
                <div key={score.name} className={`${score.bgColor} p-4 rounded-xl text-center cursor-pointer hover:shadow-md transition-shadow`} onClick={() => openLeadsTab({ score: score.name, date_from: dateFrom, date_to: dateTo })}>
                  <p className="text-3xl font-bold font-['Manrope']">{stats?.score_counts?.[score.name] || 0}</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <div className={`w-2 h-2 rounded-full ${score.color}`}></div>
                    <span className="text-sm text-slate-600 capitalize">{score.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
